import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Cake as CakeIcon,
  PersonAdd as PersonAddIcon,
  FiberManualRecord as DotIcon,
  MarkEmailRead as MarkReadIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/Common/PageHeader';
import ContentPaper from '../../components/Common/ContentPaper';
import { Notification, NotificationStatus, NotificationType } from '../../types';
import api from '../../utils/api';

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get<Notification[]>('/notifications');
      setNotifications(response.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: NotificationStatus.READ } : n))
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, status: NotificationStatus.READ })));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark all as read');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete notification');
    }
  };

  const getTypeConfig = (type: NotificationType) => {
    switch (type) {
      case NotificationType.AGENT_ASSIGNED:
        return { icon: <PersonAddIcon />, label: 'Agent Assigned', color: '#1967d2', bg: '#e8f0fe' };
      case NotificationType.BIRTHDAY:
        return { icon: <CakeIcon />, label: 'Birthday', color: '#e91e63', bg: '#fce4ec' };
      case NotificationType.PROPERTY_APPROVAL:
        return { icon: <InfoIcon />, label: 'Property Approval', color: '#0d7c3d', bg: '#e6f4ea' };
      case NotificationType.PERMISSION_REQUEST:
        return { icon: <InfoIcon />, label: 'Permission Request', color: '#f57c00', bg: '#fff3e0' };
      case NotificationType.AGENT_REQUEST:
        return { icon: <InfoIcon />, label: 'Agent Request', color: '#0288d1', bg: '#e1f5fe' };
      default:
        return { icon: <NotificationsIcon />, label: 'Notification', color: '#555', bg: '#f5f5f5' };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getInitials = (firstName?: string, lastName?: string) =>
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

  const unreadCount = notifications.filter((n) => n.status === NotificationStatus.UNREAD).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Notifications"
        subtitle={`${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
        icon={<NotificationsIcon />}
      />

      <ContentPaper>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {unreadCount > 0 && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button startIcon={<CheckCircleIcon />} onClick={handleMarkAllAsRead} variant="outlined" size="small">
              Mark All as Read
            </Button>
          </Box>
        )}

        {notifications.length === 0 ? (
          <Box textAlign="center" py={6}>
            <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No notifications</Typography>
            <Typography variant="body2" color="text.secondary">You're all caught up!</Typography>
          </Box>
        ) : (
          <Stack spacing={0} divider={<Divider />}>
            {notifications.map((n) => {
              const cfg = getTypeConfig(n.type);
              const isUnread = n.status === NotificationStatus.UNREAD;
              return (
                <Box
                  key={n.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    px: 2,
                    py: 2,
                    bgcolor: isUnread ? 'action.hover' : 'transparent',
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  {/* Type icon avatar */}
                  <Avatar sx={{ bgcolor: cfg.bg, color: cfg.color, width: 42, height: 42, mt: 0.5, flexShrink: 0 }}>
                    {cfg.icon}
                  </Avatar>

                  {/* Body */}
                  <Box flex={1} minWidth={0}>
                    {/* Top row: type chip + unread dot + time */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                      <Chip
                        label={cfg.label}
                        size="small"
                        sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                      />
                      {isUnread && (
                        <DotIcon sx={{ fontSize: 10, color: 'error.main' }} />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {formatDate(n.createdAt)}
                      </Typography>
                    </Box>

                    {/* Message */}
                    <Typography variant="body2" sx={{ fontWeight: isUnread ? 600 : 400, mb: 1 }}>
                      {n.message}
                    </Typography>

                    {/* Sender / Recipient row */}
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      {n.sender && (
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'grey.400' }}>
                            {getInitials(n.sender.firstName, n.sender.lastName)}
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">
                            From: <strong>{n.sender.firstName} {n.sender.lastName}</strong>
                            <Chip label={n.sender.role.replace('_', ' ')} size="small"
                              sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                          </Typography>
                        </Box>
                      )}
                      {n.recipient && (
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'primary.light' }}>
                            {getInitials(n.recipient.firstName, n.recipient.lastName)}
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">
                            To: <strong>{n.recipient.firstName} {n.recipient.lastName}</strong>
                            <Chip label={n.recipient.role.replace('_', ' ')} size="small"
                              sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} flexShrink={0}>
                    {isUnread && (
                      <Tooltip title="Mark as read">
                        <IconButton size="small" onClick={() => handleMarkAsRead(n.id)} sx={{ color: 'success.main' }}>
                          <MarkReadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(n.id)} sx={{ color: 'error.light' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </ContentPaper>
    </Box>
  );
};

export default NotificationsPage;
